import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProblems } from '../hooks/useProblems';
import './HomePage.css';

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
/* Each pattern has:
 *  key   — display label
 *  match — array of substrings to check against p.pattern (case-insensitive OR)
 *  icon, color
 */
const PATTERNS = [
  { key: 'Sliding Window',      match: ['sliding window'],              icon: '🪟', color: 'blue'   },
  { key: 'Binary Search',       match: ['binary search'],               icon: '🔍', color: 'orange' },
  { key: 'Dynamic Programming', match: ['dp', 'dynamic', 'limit constraint'], icon: '🧮', color: 'purple' },
  { key: 'Bit Manipulation',    match: ['bit', 'xor', 'mask'],          icon: '⚙️', color: 'gold'   },
  { key: 'Tree / DFS',          match: ['tree', 'dfs', 'path accum'],   icon: '🌲', color: 'green'  },
  { key: 'Graph / MST',         match: ['graph', 'kruskal', 'mst', 'union'], icon: '🗺️', color: 'orange' },
  { key: 'Greedy',              match: ['greedy'],                       icon: '🎯', color: 'purple' },
  { key: 'Backtracking',        match: ['backtrack'],                    icon: '🧩', color: 'gold'   },
  { key: 'Math',                match: ['math', 'modular', 'fermat'],   icon: '📐', color: 'green'  },
  { key: 'Custom Sort',         match: ['sort', 'comparator'],          icon: '↕️', color: 'blue'   },
  { key: 'Diagonal / String',   match: ['diagonal', 'cantor', 'simulation'], icon: '📐', color: 'purple' },
];

const FEATURES = [
  { icon: '📅', color: 'blue', title: 'Daily POTD',
    desc: 'Fresh LeetCode Problem of the Day added every single day. Structured explanation so you can learn consistently.' },

  { icon: '🎬', color: 'green',  title: 'Interactive Visualisations',
    desc: 'Watch the algorithm execute step-by-step. Sliding windows slide, DP tables fill, graphs grow — nothing is static.' },

  { icon: '🧠', color: 'purple', title: 'Pattern-First Thinking',
    desc: "Don't memorise solutions. Learn the pattern. Every problem maps to a reusable template that unlocks dozens more." },

  { icon: '💻', color: 'gold',   title: 'Python · C++ · Java',
    desc: 'Clean, idiomatic solutions in all three interview languages with full time & space complexity breakdowns.' },

  { icon: '🔍', color: 'orange', title: 'Step-by-Step Breakdown',
    desc: 'Every problem is broken into clear logical steps. No hand-waving, no skipping — understand exactly why the solution works.' },

  { icon: '▶️', color: 'red', title: 'YouTube Deep Explanation',
    desc: 'Each problem also includes a detailed YouTube walkthrough where the algorithm is explained visually from intuition to code.' },
];


const FLOATS = [
  { cls: 'hf--1', text: 'binary_search(lo, hi, feasible)' },
  { cls: 'hf--2', text: 'dp[i][j] = dp[i-1][j] + dp[i][j-1]' },
  { cls: 'hf--3', text: 'union_find.merge(u, v)' },
  { cls: 'hf--4', text: 'n & (n-1) == 0  →  power of 2' },
];

/* ─────────────────────────────────────────────
   HELPERS — all derived from real DB data
───────────────────────────────────────────── */
function todayISO() {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

function isoFromDate(d) {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

function latestProblem(problems) {
  if (!problems.length) return null;
  const today = todayISO();
  return (
    problems.find(p => p.date_iso === today) ||
    [...problems].sort((a, b) => (b.date_iso || '').localeCompare(a.date_iso || ''))[0]
  );
}

/** Real consecutive-day streak ending on today or most recent problem date */
function calcStreak(problems) {
  const dateSet = new Set(problems.map(p => p.date_iso).filter(Boolean));
  if (!dateSet.size) return 0;
  const today = todayISO();
  // Start from today; if no problem today start from most-recent problem date
  let cursor = new Date();
  if (!dateSet.has(today)) {
    const latest = [...dateSet].sort().pop();
    cursor = new Date(latest + 'T12:00:00');
  }
  let streak = 0;
  while (dateSet.has(isoFromDate(cursor))) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/** Problems solved in last 7 calendar days */
function calcThisWeek(problems) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  const cut = isoFromDate(cutoff);
  return problems.filter(p => p.date_iso && p.date_iso >= cut).length;
}

/** 52-cell activity heatmap — one cell per day, most-recent on right */
function buildHeatmap(problems) {
  const dateSet = new Set(problems.map(p => p.date_iso).filter(Boolean));
  return Array.from({ length: 52 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (51 - i));
    const iso = isoFromDate(d);
    return { iso, active: dateSet.has(iso) };
  });
}

function calcPatternCounts(problems) {
  const counts = {};
  problems.forEach(p => {
    const pat = (p.pattern || '').toLowerCase();
    PATTERNS.forEach(pt => {
      const matched = pt.match.some(kw => pat.includes(kw.toLowerCase()));
      if (matched) counts[pt.key] = (counts[pt.key] || 0) + 1;
    });
  });
  return counts;
}

function calcDiffCounts(problems) {
  return {
    Easy:   problems.filter(p => p.difficulty === 'Easy').length,
    Medium: problems.filter(p => p.difficulty === 'Medium').length,
    Hard:   problems.filter(p => p.difficulty === 'Hard').length,
  };
}

function diffBadgeCls(d = '') {
  const l = d.toLowerCase();
  return `badge badge--${l === 'hard' ? 'hard' : l === 'medium' ? 'medium' : 'easy'}`;
}

function patternTagCls(pat = '') {
  const p = pat.toLowerCase();
  if (p.includes('dynamic') || p.includes('dp')) return 'ptag--dp';
  if (p.includes('bit'))                          return 'ptag--bit';
  if (p.includes('graph') || p.includes('mst') || p.includes('union')) return 'ptag--graph';
  if (p.includes('tree'))                         return 'ptag--tree';
  if (p.includes('string') || p.includes('diagonal')) return 'ptag--string';
  return 'ptag--default';
}

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.rv');
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add('rv--in');
      }),
      { threshold: 0.06 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function useLiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/* ─────────────────────────────────────────────
   ANIMATED COUNTER
───────────────────────────────────────────── */
function AnimCount({ to, duration = 700 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (typeof to !== 'number') return;
    let start = null;
    const tick = ts => {
      if (!start) start = ts;
      const pct = Math.min((ts - start) / duration, 1);
      // ease-out quad
      setVal(Math.round((1 - (1 - pct) ** 2) * to));
      if (pct < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [to, duration]);
  return <>{val}</>;
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function HomePage() {
  const { problems, loading } = useProblems();
  const navigate = useNavigate();
  const now = useLiveClock();
  useReveal();

  /* All stats from real DB */
  const potd      = useMemo(() => latestProblem(problems),    [problems]);
  const patCount  = useMemo(() => calcPatternCounts(problems), [problems]);
  const diffCount = useMemo(() => calcDiffCounts(problems),    [problems]);

  /* Recent 8 problems — always sorted newest first */
  const recent = useMemo(() =>
    [...problems]
      .sort((a, b) => (b.date_iso || '').localeCompare(a.date_iso || ''))
      .slice(0, 8),
    [problems]
  );

  /* Patterns sorted by count DESC so most-used appear first */
  const sortedPatterns = useMemo(() => {
    if (loading) return PATTERNS;
    return [...PATTERNS].sort((a, b) =>
      (patCount[b.key] || 0) - (patCount[a.key] || 0)
    );
  }, [patCount, loading]);

  const dateLabel = now.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  /* Clicking a pattern navigates to /potd?pattern=key */
  function handlePatternClick(pt) {
    const count = patCount[pt.key] || 0;
    if (count === 0) return;
    navigate('/potd?pattern=' + encodeURIComponent(pt.key));
  }

  return (
    <div className="hp">

      {/* ══ HERO ══ */}
      <section className="hp__hero">
        <div className="hp__hero-bg" />
        <div className="hp__hero-grid" />
        {FLOATS.map(f => (
          <div key={f.cls} className={`hp__float ${f.cls}`}>{f.text}</div>
        ))}

        <div className="hp__hero-body">
          <div className="hp__eyebrow">
            <span className="hp__eyebrow-dot" />
            {dateLabel}
          </div>
          <h1 className="hp__h1">
            <span className="hp__h1-dim">Master the patterns.</span><br />
            <span className="hp__h1-acc">Own every interview.</span>
          </h1>
          <p className="hp__sub">
            Deep-dive explanations, interactive visualisations, and pattern-first
            thinking — built for the grind.
          </p>
          <div className="hp__cta">
            {potd
              ? <Link className="hp__btn hp__btn--primary" to={`/potd/${potd.slug}`}>🔥 Today's Problem</Link>
              : <Link className="hp__btn hp__btn--primary" to="/potd">🔥 All Problems</Link>
            }
            <Link className="hp__btn hp__btn--ghost" to="/potd">Browse All →</Link>
          </div>
        </div>

        <div className="hp__hero-stats">
          {[
            { n: problems.length, l: 'Solved'  },
            { n: diffCount.Easy,  l: 'Easy',   cls: 'easy'   },
            { n: diffCount.Medium,l: 'Medium', cls: 'medium' },
            { n: diffCount.Hard,  l: 'Hard',   cls: 'hard'   },
          ].map((s, i, a) => (
            <React.Fragment key={s.l}>
              <div className="hp__hs">
                <span className={`hp__hs-n${s.cls ? ` hp__hs-n--${s.cls}` : ''}`}>
                  {loading ? '—' : <AnimCount to={s.n} />}
                </span>
                <span className="hp__hs-l">{s.l}</span>
              </div>
              {i < a.length - 1 && <div className="hp__hs-sep" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ══ POTD BANNER ══ */}
      <section className="hp__sec rv">
      {potd && (
        <div className="hp__potd rv">
          <div className="hp__potd-tag">🗓 Today</div>
          <div className="hp__potd-title">#{potd.id} — {potd.title}</div>
          <div className="hp__potd-meta">
            <span className={diffBadgeCls(potd.difficulty)}>{potd.difficulty}</span>
            <Link className="hp__btn hp__btn--primary hp__btn--sm" to={`/potd/${potd.slug}`}>
              Solve Now →
            </Link>
          </div>
        </div>
      )}
      </section>


      {/* ══ RECENT PROBLEMS TABLE ══ */}
      <section className="hp__sec rv" style={{ paddingTop: 0 }}>
        <div className="hp__sec-hd">
          <div>
            <h2 className="hp__sec-title">Recent <span>Problems</span></h2>
            <p className="hp__sec-sub">Latest drops — click a pattern card above to filter</p>
          </div>
          <Link className="hp__sec-link" to="/potd">View all →</Link>
        </div>

        <div className="hp__tbl">
          <div className="hp__tbl-row hp__tbl-hd">
            <span>#</span><span>Title</span><span>Pattern</span><span>Diff.</span><span>Date</span>
          </div>
          {loading
            ? [...Array(5)].map((_, i) => (
                <div key={i} className="hp__tbl-row hp__tbl-skel">
                  <div className="skel" style={{ width: 38, height: 13 }} />
                  <div className="skel" style={{ width: '55%', height: 13 }} />
                  <div className="skel" style={{ width: 90, height: 20, borderRadius: 20 }} />
                  <div className="skel" style={{ width: 55, height: 20, borderRadius: 20 }} />
                  <div className="skel" style={{ width: 50, height: 13 }} />
                </div>
              ))
            : recent.map(p => (
                <Link key={p.id} className="hp__tbl-row hp__tbl-data" to={`/potd/${p.slug}`}>
                  <span className="hp__tbl-num">{p.id}</span>
                  <span className="hp__tbl-title">{p.title}</span>
                  <span>
                    <span className={`hp__ptag ${patternTagCls(p.pattern)}`}>
                      {(p.pattern || '—').split(' + ')[0]}
                    </span>
                  </span>
                  <span><span className={diffBadgeCls(p.difficulty)}>{p.difficulty}</span></span>
                  <span className="hp__tbl-date">
                    {p.date_iso
                      ? new Date(p.date_iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : p.date || '—'}
                  </span>
                </Link>
              ))
          }
        </div>
      </section>

      {/* ══ FEATURES ══ */}
      <div className="hp__divider" />
      <section className="hp__sec rv">
        <div className="hp__sec-hd" style={{ marginBottom: 24 }}>
          <h2 className="hp__sec-title">Why <span>Code Crusaders?</span></h2>
        </div>
        <div className="hp__feat-grid">
          {FEATURES.map(f => (
            <div key={f.title} className="hp__feat">
              <div className={`hp__feat-icon hp__feat-icon--${f.color}`}>{f.icon}</div>
              <div className="hp__feat-title">{f.title}</div>
              <div className="hp__feat-desc">{f.desc}</div>
              <Link className="hp__feat-link" to="/potd">Explore →</Link>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}
